import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import {
  clearCreateDocumentExcelTransaction,
  clearDocumentExcelAccUpload,
  clearDocumentExcelUploadStatus,
  deleteDraftAccTransactionImport,
  editDraftAccTransactionImport,
  requestCreateDocumentExcelTransaction,
  requestGetAccountingTransactionImport,
  requestListAccount,
  setAccTransactionInitialData,
  useAccountingDispatch,
  useAccountingState,
  usePeriodLock,
} from "../../Context";
import {
  convertToTitleCase,
  getAccountingFileName,
  getErrorMessages,
  getPeriodClosedError,
  getPeriodClosedRows,
  isDateInClosedPeriod,
} from "../../Utils";
import PeriodLockBanner from "../../Components/v2/Accounting/PeriodLockBanner";
import PeriodClosedNotice from "../../Components/v2/Accounting/PeriodClosedNotice";
import PeriodRowNote from "../../Components/v2/Accounting/PeriodRowNote";
import Badge from "../../Components/Badge";
import { Button } from "../../Components/Button";
import { Alert } from "../../Components/Alert";
import ErrorStateTable from "../../Components/ErrorStateTable";
import { TRANSACTION_TYPES } from "../../Constants";
import { PencilAltIcon, TrashIcon } from "@heroicons/react/outline";
import { Modal } from "../../Components/Modal";
import FormLabel from "../../Components/FormLabel";
import JournalEntryTable from "../../Components/JournalEntryTable";
import { ModalAlert } from "../../Components/ModalAlert";
import { ModalConfirm } from "../../Components/ModalConfirm";
import TableSkeleton from "../../Components/TableSkeleton";

const AccountingTransactionImportDraft = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const accountingDispatch = useAccountingDispatch();
  const {
    draftImport,
    draftImportLoading,
    draftImportError,
    accounts,
    allAccountLoading,
    excelTransactionsLoading,
    excelTransactionsError,
    excelTransactionsSuccess,
    excelTransactions,
  } = useAccountingState();

  const {
    minDate: periodMinDate,
    closedThrough,
    refetch: refetchPeriod,
  } = usePeriodLock(draftImport?.businessID);

  // A spreadsheet has no date picker, so per-row rejection *is* the interface.
  //
  // Unlike the upload screen, there is no server row list to read here: the
  // stored record keeps only `errors`, and the rows a save would refuse are not
  // part of it. So the rows are derived from the boundary itself — every draft
  // row carries its own date, and isDateInClosedPeriod is the same test the
  // pickers elsewhere use. Deriving it also means the mark tracks the *current*
  // boundary rather than whatever was true when the file was parsed, which is
  // the point for a draft opened days later.
  //
  // Declared above the effects because periodRejection feeds a dependency array
  // below, which is evaluated during render.
  const periodRows = useMemo(() => {
    const rows = {};
    if (closedThrough) {
      (draftImport?.transactions || []).forEach((item, index) => {
        if (!isDateInClosedPeriod(item?.createdAt, closedThrough)) return;
        // No message of its own: the boundary is stated by the banner above,
        // and the row falls back to the generic wording.
        rows[index] = null;
      });
    }

    // Where the server has ruled, its verdict replaces the guess.
    return { ...rows, ...getPeriodClosedRows(excelTransactionsError) };
  }, [closedThrough, draftImport?.transactions, excelTransactionsError]);

  // Memoised on the error itself: a fresh identity every render would re-fire
  // the modal effect that depends on it.
  const periodRejection = useMemo(
    () => getPeriodClosedError(excelTransactionsError),
    [excelTransactionsError],
  );

  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });
  const [showConfirmModal, setshowConfirmModal] = useState(false);
  const [showEditModal, setshowEditModal] = useState(false);
  const [accountingTransactiontId, setaccountingTransactiontId] = useState("");
  const [accountItems, setaccountItems] = useState([
    {
      account: "",
      debit: { value: "0", floatValue: 0 },
      credit: { value: "0", floatValue: 0 },
    },
  ]);
  const [date, setdate] = useState(new Date());
  const [type, settype] = useState(null);
  const [note, setnote] = useState("");
  const [inputErrors, setinputErrors] = useState({});
  const [accountItemsError, setaccountItemsError] = useState("");
  const isFormSubmit = useRef(false);

  const transactionTypes = useMemo(() => {
    // System-generated journals are posted by the backend, not entered by hand.
    const selectableTransactionTypes = TRANSACTION_TYPES.filter(
      (item) => !item.systemGenerated
    );

    return selectableTransactionTypes.map((item) => ({
      ...item,
      label: t(item.label),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TRANSACTION_TYPES, t]);

  useEffect(() => {
    return () => {
      clearDocumentExcelUploadStatus(accountingDispatch);
      clearCreateDocumentExcelTransaction(accountingDispatch);
    };
  }, [accountingDispatch]);

  useEffect(() => {
    if (id) {
      requestGetAccountingTransactionImport(accountingDispatch, {
        id: id,
      });
    }
  }, [id, accountingDispatch]);

  useEffect(() => {
    if (draftImport?.businessID) {
      requestListAccount(accountingDispatch, {
        businessID: draftImport.businessID,
        loadAll: true,
      });
    }
  }, [draftImport?.businessID, accountingDispatch]);

  useEffect(() => {
    if (excelTransactionsSuccess) {
      setModal({
        visible: true,
        title: t("Success"),
        description: t("Your transaction has been created."),
        type: "SUCCESS",
      });
    } else if (excelTransactionsError && !periodRejection) {
      // A closed period is a status, not a failure: it gets the amber notice
      // above the table and the per-row marks, never a red alert modal.
      setModal({
        visible: true,
        title: t("Error"),
        description: t(getErrorMessages(excelTransactionsError)),
        type: "ALERT",
      });
    }
  }, [excelTransactionsSuccess, excelTransactionsError, periodRejection, t]);

  const handleModalConfirm = () => {
    if (excelTransactionsSuccess && excelTransactions?.length > 0) {
      const { startDate, endDate } = excelTransactions.reduce(
        (acc, transaction) => {
          const currentDate = new Date(transaction.createdAt);
          if (currentDate < acc.startDate) acc.startDate = currentDate;
          if (currentDate > acc.endDate) acc.endDate = currentDate;
          return acc;
        },
        {
          startDate: new Date(excelTransactions[0].createdAt),
          endDate: new Date(excelTransactions[0].createdAt),
        }
      );

      setAccTransactionInitialData(accountingDispatch, {
        transactionInitialData: {
          dateFilter: "OTHER",
          startDate: startDate,
          endDate: endDate,
        },
      });
      navigate("/v2/accounting");
      clearDocumentExcelAccUpload(accountingDispatch);
    } else {
      setModal((prevModal) => ({
        ...prevModal,
        visible: false,
      }));
    }
  };

  const validateForm = () => {
    let hasErrors = false;
    const validationErrors = {};

    const validateRequiredField = (fieldName, fieldValue, displayFieldName) => {
      if (!fieldValue) {
        validationErrors[fieldName] = `${displayFieldName} is required.`;
        hasErrors = true;
      }
    };

    validateRequiredField("transactionType", type, "Transaction type");

    validateRequiredField("date", date, "Date");

    const validateAccount = accountItems.every((entry) => {
      return entry.account.value;
    });
    const validateDebitAmount = accountItems.every((entry) => {
      return entry.debit.floatValue !== undefined;
    });
    const validateCreditAmount = accountItems.every((entry) => {
      return entry.credit.floatValue !== undefined;
    });

    validateRequiredField("accountItems", validateAccount, "Account");
    validateRequiredField("debitAmount", validateDebitAmount, "Debit amount");
    validateRequiredField(
      "creditAmount",
      validateCreditAmount,
      "Credit amount"
    );

    for (const entry of accountItems) {
      const debit = entry.debit.floatValue;
      const credit = entry.credit.floatValue;

      if (!((debit !== 0 && credit === 0) || (debit === 0 && credit !== 0))) {
        validationErrors["debitCredit"] =
          "Debit and Credit amounts are invalid.";
        setinputErrors(validationErrors);
        return;
      }
    }

    const encounteredIds = new Set();
    const totalDebit = accountItems.reduce((acc, { account, debit }) => {
      if (encounteredIds.has(account.value)) {
        setaccountItemsError("Account cannot be the same.");
        hasErrors = true;
      } else {
        encounteredIds.add(account.value);
      }
      return acc + debit.floatValue;
    }, 0);

    const totalCredit = accountItems.reduce(
      (acc, { credit }) => acc + credit.floatValue,
      0
    );

    if (totalDebit !== totalCredit) {
      setaccountItemsError("Total debit and credit must be the same.");
      hasErrors = true;
    }

    setinputErrors(validationErrors);
    return !hasErrors;
  };

  const onSaveEditDraft = () => {
    isFormSubmit.current = true;

    if (validateForm()) {
      const accountInputs = accountItems.map(({ account, debit, credit }) => ({
        ...account,
        debit: debit.floatValue,
        credit: credit.floatValue,
      }));

      const input = {
        id: accountingTransactiontId,
        type: type.value,
        createdAt: date.toISOString(),
        description: note || "",
        accounts: accountInputs,
      };
      editDraftAccTransactionImport(accountingDispatch, {
        draftItem: input,
      });
      setshowEditModal(false);
      setinputErrors({});
      setaccountingTransactiontId("");
      setaccountItemsError("");
    }
  };

  const handleAddRow = () => {
    if (accountItems.length < 100) {
      setaccountItems([
        ...accountItems,
        {
          account: "",
          debit: { value: "0", floatValue: 0 },
          credit: { value: "0", floatValue: 0 },
        },
      ]);
    }
  };

  const handleRemoveRow = (index) => {
    const newAccountItems = [...accountItems];
    newAccountItems.splice(index, 1);
    setaccountItems(newAccountItems);
  };

  const updateAccountItem = (index, updatedProperties) => {
    return (prevState) => {
      const updatedItems = [...prevState];

      updatedItems[index] = {
        ...updatedItems[index],
        ...updatedProperties,
      };

      return updatedItems;
    };
  };

  const renderEditModal = () => {
    return (
      <Modal
        show={showEditModal}
        onClose={() => {
          setshowEditModal(false);
          setinputErrors({});
          setaccountingTransactiontId("");
          setaccountItemsError("");
        }}
        title={t("Change Accounting Account")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button onClick={onSaveEditDraft} type="submit" title="Save" />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          <div>
            <FormLabel htmlFor="type" label={t("Transaction Type")} required />
            <Select
              name="type"
              className="mt-1 text-sm"
              classNamePrefix="select2-selection"
              options={transactionTypes}
              placeholder={t("Select Transaction Type")}
              value={type}
              onChange={settype}
              maxMenuHeight={150}
            />
            {inputErrors.transactionType && (
              <div className="mt-1 text-red-600 text-sm">
                {t(inputErrors.transactionType)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="date" label={t("Date")} required />
            {/* Row-edit modal: constraining it here is how a rejected row gets
                corrected rather than merely reported. */}
            <ReactDatePicker
              name="date"
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              selected={date}
              onChange={setdate}
              placeholderText={t("Select Date")}
              dateFormat="dd/MM/yyyy"
              minDate={periodMinDate}
            />
            {inputErrors.date && (
              <div className="mt-1 text-red-600 text-sm">
                {t(inputErrors.date)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="note" label={t("Note")} />
            <input
              type="text"
              name="note"
              value={note}
              placeholder={t("Enter Transaction Note")}
              onChange={(e) => setnote(e.target.value)}
              className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <div>
            <JournalEntryTable
              accountItems={accountItems}
              setaccountItems={setaccountItems}
              accounts={accounts}
              allAccountLoading={allAccountLoading}
              inputErrors={inputErrors}
              isFormSubmit={isFormSubmit}
              updateAccountItem={updateAccountItem}
              handleRemoveRow={handleRemoveRow}
              handleAddRow={handleAddRow}
              selectedBusiness={draftImport?.businessID}
              accountingDispatch={accountingDispatch}
              requestListAccount={requestListAccount}
              editModal={true}
              accountItemsError={accountItemsError}
            />
          </div>
        </div>
      </Modal>
    );
  };

  const onDelete = () => {
    setshowConfirmModal(false);
    setaccountingTransactiontId("");
    deleteDraftAccTransactionImport(accountingDispatch, {
      accountingTransactiontId,
    });
  };

  const onCreate = () => {
    const transactions = draftImport?.transactions?.map((item) => {
      const total = item.accounts.reduce(
        (sum, account) => sum + account.debit,
        0
      );
      const accounts = item.accounts.map(({ id, credit, debit }) => ({
        id,
        credit,
        debit,
      }));

      return {
        created_at: item.createdAt,
        type: item.type,
        description: item.description,
        total,
        accounts,
      };
    });

    requestCreateDocumentExcelTransaction(accountingDispatch, {
      businessID: draftImport?.businessID,
      transactions,
    });
  };

  const parsedErrorResponse = excelTransactionsError?.response?.body
    ? JSON.parse(excelTransactionsError.response.body)
    : null;

  const newExcelTransactionsError = parsedErrorResponse?.error;

  if (draftImportError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={draftImportError?.message}
          errors={draftImportError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm sm:overflow-hidden rounded-md sm:rounded-lg">
        {renderEditModal()}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => {
            setshowConfirmModal(open);
          }}
          title="deleteDraftTransactionTitle"
          description="accountingTransactionDescription"
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          onConfirm={onDelete}
        />
        <ModalConfirm
          open={modal.visible}
          setOpen={(open) => {
            setModal((prevModal) => ({
              ...prevModal,
              visible: open,
            }));
          }}
          title={modal.title}
          description={modal.description}
          type={modal.type}
          onConfirm={() => handleModalConfirm()}
        />
        <div className="py-4 px-4 sm:px-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex sm:flex-row flex-col gap-4">
              {draftImportLoading ? (
                <div className="flex flex-col animate-pulse py-1 gap-1">
                  <div className="flex items-center gap-2">
                    <div className="w-36 h-4 bg-gray-300 rounded-md" />
                    <div className="w-14 h-4 bg-gray-300 rounded-md" />
                  </div>
                  <div className="py-1 w-60 h-4 bg-gray-300 rounded-md" />
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="text-sm font-medium text-gray-900">
                      {getAccountingFileName(draftImport.file?.key)}
                    </span>
                    <div className="text-sm">
                      {draftImport.status === "PENDING" ? (
                        <Badge title="Pending" type="alert" />
                      ) : null}
                      {draftImport.status === "SUCCESS" ? (
                        <Badge title="Success" type="success" />
                      ) : null}
                      {draftImport.status === "FAILED" ? (
                        <Badge title="Failed" type="danger" />
                      ) : null}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("created")}{" "}
                    {dayjs(draftImport.createdAt)
                      .locale(i18n.language)
                      .format("DD MMMM YYYY")}
                    {" • "} {t("Expired")}{" "}
                    {dayjs(draftImport.expiredAt)
                      .locale(i18n.language)
                      .format("DD MMMM YYYY")}
                  </div>
                </div>
              )}
            </div>
            <div className="flex sm:flex-row flex-col w-full sm:w-auto gap-4">
              <Button
                className="w-full md:w-auto"
                type="button"
                disabled={draftImportLoading}
                title={t("importHistoryTitle")}
                onClick={() => {
                  navigate(
                    `/accounting-transaction/draft?business=${draftImport?.businessID}`
                  );
                }}
                outline
              />
              {draftImport.status === "SUCCESS" && (
                <Button
                  className="w-full md:w-auto"
                  type="button"
                  disabled={
                    draftImportLoading ||
                    excelTransactionsLoading ||
                    !draftImport?.transactions?.length
                    // Marked rows deliberately do NOT disable this. Here the
                    // marks are the client's own reading of the boundary, so
                    // blocking on them would strand a draft the server would
                    // actually accept — after a reopen, say. The save is
                    // all-or-nothing and /bulk is the authority on it.
                  }
                  loading={excelTransactionsLoading}
                  title={t("Create Accounting Transaction")}
                  onClick={onCreate}
                />
              )}
            </div>
          </div>
        </div>
        {draftImport.id && draftImport.status !== "SUCCESS" && (
          <div className="space-y-6 px-4 py-5 sm:p-6">
            {draftImport.status === "PENDING" && (
              <Alert type="info" message="documentProcessedTitle" />
            )}
            {draftImport.status === "FAILED" && (
              <Alert
                type="error"
                title={t("draftImportErrorTitle", {
                  count: draftImport.errors.length,
                })}
                messages={draftImport.errors}
              />
            )}
          </div>
        )}
      </div>
      <PeriodLockBanner businessID={draftImport?.businessID} />
      {/* Once the save has been refused: the server's own wording, and the
          refresh of the cached boundary the marks above were derived from.
          Before that the banner states the boundary and the date cells carry
          the per-row reasons. */}
      <PeriodClosedNotice
        error={excelTransactionsError}
        onRefresh={refetchPeriod}
      />
      {draftImport.status === "SUCCESS" && (
        <div className="flex flex-col">
          <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Date")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Transaction Type")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Description")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Account")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Debit")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Credit")}
                      </th>
                      <th className="px-4 py-2 font-medium text-sm text-left text-gray-900 whitespace-nowrap">
                        {t("Action")}
                      </th>
                    </tr>
                  </thead>
                  {draftImportLoading ? (
                    <TableSkeleton rows={3} columns={7} />
                  ) : (
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {draftImport?.transactions?.length > 0 ? (
                        draftImport?.transactions?.map((item, index) => {
                          const transactionErrors =
                            draftImport?.errors?.[index];
                          const createdAtError =
                            transactionErrors?.created_at?.[0];
                          const newTransactionError =
                            newExcelTransactionsError?.transactions?.[index];
                          return (
                            <React.Fragment key={index}>
                              {item?.accounts?.map((account, accountIndex) => {
                                const accountErrors =
                                  transactionErrors?.accounts?.[accountIndex];
                                const codeError = accountErrors?.code?.[0];
                                return (
                                  <tr
                                    key={`${index}-${accountIndex}`}
                                    className={
                                      accountIndex > 0 ? "border-none" : ""
                                    }
                                  >
                                    {accountIndex === 0 && (
                                      <>
                                        <td
                                          className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                          rowSpan={item.accounts.length}
                                        >
                                          {/* The old `&& !item.createdAt` guard
                                              hid the message whenever the date
                                              parsed fine — exactly the case for
                                              a period-rejected row, which has a
                                              perfectly valid date. */}
                                          {createdAtError && !item.createdAt ? (
                                            <span className="text-red-500">
                                              {createdAtError}
                                            </span>
                                          ) : (
                                            <>
                                              {dayjs(item.createdAt)
                                                .locale(i18n.language)
                                                .format("DD MMM YYYY")}
                                              {periodRows[index] !==
                                                undefined && (
                                                <PeriodRowNote
                                                  message={periodRows[index]}
                                                />
                                              )}
                                            </>
                                          )}
                                        </td>
                                        <td
                                          className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                          rowSpan={item.accounts.length}
                                        >
                                          {t(convertToTitleCase(item.type))}
                                        </td>
                                        <td
                                          className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
                                          rowSpan={item.accounts.length}
                                        >
                                          {item.description}
                                        </td>
                                      </>
                                    )}
                                    <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap">
                                      {codeError && !account.name ? (
                                        <span className="text-red-500">
                                          {codeError}
                                        </span>
                                      ) : (
                                        `${account.name} - ${account.code}`
                                      )}
                                    </td>
                                    <td className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap">
                                      <NumericFormat
                                        displayType={"text"}
                                        value={account.debit}
                                        thousandSeparator={"."}
                                        decimalSeparator={","}
                                        prefix={"Rp"}
                                      />
                                    </td>
                                    <td className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap">
                                      <NumericFormat
                                        displayType={"text"}
                                        value={account.credit}
                                        thousandSeparator={"."}
                                        decimalSeparator={","}
                                        prefix={"Rp"}
                                      />
                                    </td>
                                    {accountIndex === 0 && (
                                      <td
                                        className="px-4 py-2 font-medium text-right text-sm text-gray-900 whitespace-nowrap"
                                        rowSpan={item.accounts.length}
                                      >
                                        <div className="flex flex-auto gap-2">
                                          {excelTransactionsLoading ? (
                                            <div className="w-5 h-5 bg-gray-300 rounded-md" />
                                          ) : (
                                            <button
                                              onClick={() => {
                                                const selectedType =
                                                  transactionTypes.find(
                                                    (type) =>
                                                      type.value === item.type
                                                  );

                                                const mappedItems =
                                                  item?.accounts?.map(
                                                    (item) => {
                                                      return {
                                                        account: {
                                                          ...item,
                                                          label: `${item?.code} - ${item?.name}`,
                                                          value: item.id,
                                                        },
                                                        debit: {
                                                          value: `${item.debit}`,
                                                          floatValue:
                                                            item.debit,
                                                        },
                                                        credit: {
                                                          value: `${item.credit}`,
                                                          floatValue:
                                                            item.credit,
                                                        },
                                                      };
                                                    }
                                                  );
                                                setshowEditModal(true);
                                                settype(selectedType);
                                                setdate(
                                                  new Date(item.createdAt)
                                                );
                                                setnote(item.description);
                                                setaccountItems(mappedItems);
                                                setaccountingTransactiontId(
                                                  item.id
                                                );
                                              }}
                                            >
                                              <PencilAltIcon className="h-5 w-5 text-blue-500" />
                                            </button>
                                          )}
                                          {excelTransactionsLoading ? (
                                            <div className="w-5 h-5 bg-gray-300 rounded-md" />
                                          ) : (
                                            <button
                                              onClick={() => {
                                                setshowConfirmModal(true);
                                                setaccountingTransactiontId(
                                                  item.id
                                                );
                                              }}
                                            >
                                              <TrashIcon className="h-5 w-5 text-red-500" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                              {newTransactionError?.accounts?.[0]?.id && (
                                <tr className="text-red-500 border-none">
                                  {[...Array(3)].map((_, index) => (
                                    <td
                                      key={index}
                                      className="px-4 py-2 font-medium text-sm whitespace-nowrap"
                                    />
                                  ))}
                                  <td className="px-4 py-2 font-medium text-sm whitespace-nowrap">
                                    {newTransactionError.accounts[0].id}
                                  </td>
                                  {[...Array(3)].map((_, index) => (
                                    <td
                                      key={index}
                                      className="px-4 py-2 font-medium text-sm whitespace-nowrap"
                                    />
                                  ))}
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <ErrorStateTable
                          colSpan={7}
                          error={draftImportError}
                          emptyTitle="No import data"
                        />
                      )}
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingTransactionImportDraft;
